import { useEffect, useRef, useState } from 'react';
import {
  Copy,
  Film,
  FileDown,
  Pause,
  Play,
  Plus,
  SkipBack,
  SkipForward,
  Trash2,
} from 'lucide-react';
import { useEditor } from '../../state/store';
import { TuiDocument } from '../../core/Document';
import { TRANSPARENT } from '../../core/types';
import {
  AnimationFrame,
  FPS_PRESETS,
  frameDurationMs,
  playbackOrder,
  renderFrame,
  totalDurationMs,
} from '../../animation/model';
import { exportAnimation } from '../../animation/export';
import { formatBytes } from '../../ansi/Optimizer';
import { Checkbox, Hint, NumberInput, Panel } from '../widgets/common';
import { Select } from '../widgets/primitives';
import { AnimationExportDialog } from '../dialogs/AnimationExportDialog';

/**
 * Timeline.
 *
 * Frames are shown as thumbnails rather than a list of names, because the only
 * question that matters while animating is "what does this frame look like".
 * Playback drives the real canvas through layer visibility, so what plays here
 * is exactly what exports.
 */
export function AnimationPanel(): JSX.Element {
  const revision = useEditor((s) => s.revision);
  const playing = useEditor((s) => s.playing);
  const activeAnimationId = useEditor((s) => s.activeAnimationId);
  const currentFrameIndex = useEditor((s) => s.currentFrameIndex);
  const store = useEditor.getState();
  const tab = store.active();
  const [exporting, setExporting] = useState(false);

  const animation = tab.animations.find((a) => a.id === activeAnimationId) ?? null;
  const order = animation ? playbackOrder(animation) : [];

  // Playback timer. The delay is recomputed per frame so per-frame durations
  // are honoured rather than averaged.
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!playing || !animation || order.length === 0) return;

    const frame = order[Math.min(currentFrameIndex, order.length - 1)];
    timerRef.current = window.setTimeout(() => {
      const state = useEditor.getState();
      const next = state.currentFrameIndex + 1;
      if (!animation.loop && next >= order.length) {
        state.pauseAnimation();
        return;
      }
      state.gotoFrame(next);
    }, frameDurationMs(animation, frame));

    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [playing, currentFrameIndex, animation, order.length, revision]);

  if (tab.animations.length === 0) {
    return (
      <Panel title="Animation" collapsible={false}>
        <div className="empty-state">
          <Film size={22} />
          <span>No animation yet</span>
        </div>
        <button
          className="btn primary tall"
          style={{ width: '100%', marginTop: 10 }}
          onClick={() => store.createAnimation()}
        >
          <Plus size={15} /> Create animation
        </button>
        <Hint>
          How it works: a <b>frame</b> is just a set of visible layers. Draw the parts that change on
          their own layers, keep shared artwork (borders, background) on one layer, and each frame
          picks which layers to show. Nothing is duplicated, so editing the border fixes every frame.
        </Hint>
      </Panel>
    );
  }

  const stats =
    animation && animation.frames.length
      ? exportAnimation(tab.doc, animation, {
          colorMode: tab.doc.colorMode,
          background: tab.doc.background,
        })
      : null;

  return (
    <>
      <Panel title="Animation" collapsible={false}>
        <div className="row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <Select
              ariaLabel="Animation"
              placeholder="Choose an animation…"
              value={activeAnimationId ?? ''}
              options={tab.animations.map((a) => ({
                value: a.id,
                label: a.name,
                hint: `${a.frames.length} frame${a.frames.length === 1 ? '' : 's'}`,
              }))}
              onChange={(id) => store.setActiveAnimation(id || null)}
            />
          </div>
          <button className="icon-btn" title="New animation" onClick={() => store.createAnimation()}>
            <Plus size={14} />
          </button>
          {animation ? (
            <button
              className="icon-btn"
              title="Delete animation"
              onClick={() => store.removeAnimation(animation.id)}
            >
              <Trash2 size={13} />
            </button>
          ) : null}
        </div>
      </Panel>

      {animation ? (
        <>
          <Panel title="Playback" collapsible={false}>
            <div className="row">
              <button className="btn solid" onClick={() => store.stepFrame(-1)} title="Previous frame">
                <SkipBack size={14} />
              </button>
              <button
                className="btn primary tall"
                style={{ flex: 1 }}
                onClick={() => (playing ? store.pauseAnimation() : store.playAnimation())}
              >
                {playing ? <Pause size={15} /> : <Play size={15} />}
                {playing ? 'Pause' : 'Play'}
              </button>
              <button className="btn solid" onClick={() => store.stepFrame(1)} title="Next frame">
                <SkipForward size={14} />
              </button>
            </div>

            <div className="row" style={{ marginTop: 9 }}>
              <span className="faint" style={{ width: 30 }}>
                FPS
              </span>
              <NumberInput
                value={animation.fps}
                min={1}
                max={60}
                onChange={(v) => store.updateAnimation(animation.id, { fps: v })}
              />
              <div className="segmented" style={{ flex: 2 }}>
                {FPS_PRESETS.map((fps) => (
                  <button
                    key={fps}
                    className={animation.fps === fps ? 'active' : ''}
                    onClick={() => store.updateAnimation(animation.id, { fps })}
                  >
                    {fps}
                  </button>
                ))}
              </div>
            </div>

            <div className="row">
              <Checkbox
                label="Loop"
                checked={animation.loop}
                onChange={(v) => store.updateAnimation(animation.id, { loop: v })}
              />
              <Checkbox
                label="Ping-pong"
                checked={animation.pingPong}
                onChange={(v) => store.updateAnimation(animation.id, { pingPong: v })}
                title="Play forwards then backwards without repeating the end frames"
              />
            </div>
            <Hint>
              Frame {Math.min(currentFrameIndex + 1, Math.max(order.length, 1))} of {order.length} ·{' '}
              {(totalDurationMs(animation) / 1000).toFixed(2)}s total
            </Hint>
          </Panel>

          <Panel title={`Frames (${animation.frames.length})`} collapsible={false}>
            <div className="frame-strip">
              {animation.frames.map((frame, i) => (
                <FrameCard
                  key={frame.id}
                  doc={tab.doc}
                  frame={frame}
                  index={i}
                  active={i === currentFrameIndex}
                  durationMs={frameDurationMs(animation, frame)}
                  onSelect={() => store.gotoFrame(i)}
                  onDuplicate={() => store.duplicateFrame(frame.id)}
                  onDelete={() => store.removeFrame(frame.id)}
                  revision={revision}
                />
              ))}
            </div>

            <div className="grid2" style={{ marginTop: 4 }}>
              <button
                className="btn solid"
                onClick={() => store.addFrameWithLayer()}
                title="Adds an empty layer and a frame that shows it, keeping the shared layers"
              >
                <Plus size={14} /> New frame
              </button>
              <button
                className="btn solid"
                onClick={() => store.captureFrame()}
                title="Makes a frame from whichever layers are visible right now"
              >
                <Copy size={14} /> Capture view
              </button>
            </div>
            <Hint>
              <b>New frame</b> gives you a blank layer to draw the next step on.{' '}
              <b>Capture view</b> snapshots the layers you have toggled on.
            </Hint>
          </Panel>

          {animation.frames[currentFrameIndex] ? (
            <Panel title={`Layers in frame ${currentFrameIndex + 1}`}>
              {[...tab.doc.layers].reverse().map((layer) => (
                <Checkbox
                  key={layer.id}
                  label={layer.name}
                  checked={animation.frames[currentFrameIndex].layers.includes(layer.id)}
                  onChange={() =>
                    store.toggleFrameLayer(animation.frames[currentFrameIndex].id, layer.id)
                  }
                />
              ))}
              <Hint>Tick a layer to include it in this frame. Shared layers stay ticked everywhere.</Hint>
            </Panel>
          ) : null}

          {stats ? (
            <Panel title="Output">
              <div className="dim" style={{ fontSize: 11, lineHeight: 1.7 }}>
                <div>
                  Average frame <b>{formatBytes(stats.averageFrameBytes)}</b>, peak{' '}
                  {formatBytes(stats.peakFrameBytes)}
                </div>
                <div>
                  ≈ <b>{formatBytes(stats.bytesPerSecond)}/s</b> of terminal output
                </div>
              </div>
              {stats.warning ? <div className="warn-text">⚠ {stats.warning}</div> : null}
              <button
                className="btn primary tall"
                style={{ width: '100%', marginTop: 9 }}
                disabled={animation.frames.length === 0}
                onClick={() => setExporting(true)}
              >
                <FileDown size={15} /> Export animation
              </button>
            </Panel>
          ) : null}
        </>
      ) : null}

      {exporting && animation ? (
        <AnimationExportDialog animation={animation} onClose={() => setExporting(false)} />
      ) : null}
    </>
  );
}

/**
 * Thumbnail of a single frame.
 *
 * Painted as coloured blocks rather than text: at this size glyphs are
 * illegible, but the shape and colour of the frame read instantly.
 */
function FrameCard({
  doc,
  frame,
  index,
  active,
  durationMs,
  onSelect,
  onDuplicate,
  onDelete,
  revision,
}: {
  doc: TuiDocument;
  frame: AnimationFrame;
  index: number;
  active: boolean;
  durationMs: number;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  revision: number;
}): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    ctx.fillStyle = `#${(doc.background & 0xffffff).toString(16).padStart(6, '0')}`;
    ctx.fillRect(0, 0, w, h);

    const grid = renderFrame(doc, frame);
    const sx = w / grid.width;
    const sy = h / grid.height;
    for (let y = 0; y < grid.height; y++) {
      for (let x = 0; x < grid.width; x++) {
        const cell = grid.get(x, y);
        const painted = cell.char !== '' && cell.char !== ' ';
        if (!painted && cell.bg === TRANSPARENT) continue;
        const color = painted
          ? cell.fg === TRANSPARENT
            ? 0xc8ccd4
            : cell.fg
          : cell.bg;
        ctx.fillStyle = `#${(color & 0xffffff).toString(16).padStart(6, '0')}`;
        ctx.fillRect(Math.floor(x * sx), Math.floor(y * sy), Math.ceil(sx), Math.ceil(sy));
      }
    }
  }, [doc, frame, revision]);

  return (
    <div className={`frame-card${active ? ' active' : ''}`} onClick={onSelect}>
      <canvas ref={ref} width={90} height={52} />
      <div className="meta">
        <b title={frame.name}>{index + 1}</b>
        <span>{durationMs}ms</span>
        <span className="spacer" />
        <button
          className="icon-btn"
          style={{ width: 16, height: 16 }}
          title="Duplicate frame"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
        >
          <Copy size={11} />
        </button>
        <button
          className="icon-btn"
          style={{ width: 16, height: 16 }}
          title="Delete frame"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}
